import { forwardRef } from 'react';
import { Country, getCountryCallingCode } from 'react-phone-number-input';

import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  Typography,
} from '@mui/material';

import type { Override } from '@/types/common';

import { Flag } from './Flag';

type TCountryOption = {
  value?: Country;
  label: string;
};

type TPhoneCountryProps = Override<
  SelectProps,
  {
    value: Country;
    onChange: (country: string) => void;
    options: TCountryOption[];
    iconComponent: unknown;
  }
>;

const renderValue = (country: Country | '') => {
  if (!country) {
    return null;
  }

  return (
    <Grid alignItems="center">
      <Typography marginRight={1}>+{getCountryCallingCode(country)}</Typography>
      <Flag country={country} fontSize="small" />
    </Grid>
  );
};

const renderOption = ({ label, value: country }: TCountryOption) => {
  if (!country) {
    return null;
  }

  return (
    <MenuItem key={country} value={country}>
      <Flag sx={{ marginRight: 1.5 }} country={country} />
      <Typography>
        {label} (+{getCountryCallingCode(country)})
      </Typography>
    </MenuItem>
  );
};

// iconComponent and defaultValue should not be included into select`s props
export const PhoneCountry = forwardRef<HTMLSelectElement, TPhoneCountryProps>(
  ({ value, label, labelId, options, onChange, iconComponent, defaultValue, ...props }, ref) => (
    <FormControl margin="none" fullWidth={false} sx={{ flex: '0 0 100px', marginRight: 2 }}>
      <InputLabel>{label}</InputLabel>
      <Select<Country>
        inputRef={ref}
        renderValue={renderValue}
        value={value || defaultValue}
        onChange={({ target }) => onChange(target.value)}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          MenuListProps: {
            sx: {
              maxHeight: 400,
              overflow: 'auto',
            },
          },
        }}
        {...props}
      >
        {options.map(renderOption)}
      </Select>
    </FormControl>
  ),
);
